// 精度
const ACCURACY = 1.0e6

/**
 * 正态分布函数
 * @param {number} x
 * @returns {number}
 */
export function n(x) {
  return (1 / Math.sqrt(2 * Math.PI)) * Math.exp((-x * x) / 2)
}

/**
 * 单变量正态分布概率
 * @param {number} z
 * @returns {number}
 */
export function N(z) {
  if (z > 6.0) {
    return 1.0
  }
  if (z < -6.0) {
    return 0.0
  }
  const b1 = 0.31938153
  const b2 = -0.356563782
  const b3 = 1.781477937
  const b4 = -1.821255978
  const b5 = 1.330274429
  const p = 0.2316419
  const c2 = 0.3989423
  const a = Math.abs(z)
  const t = 1.0 / (1.0 + a * p)
  const b = c2 * Math.exp(-z * (z / 2.0))
  let n = ((((b5 * t + b4) * t + b3) * t + b2) * t + b1) * t
  n = 1.0 - b * n
  if (z < 0.0) n = 1.0 - n
  return n
}

/**
 * 计算期权 call 价格
 * @param {number} S 期权相关股票价格
 * @param {number} X 期权行权价
 * @param {number} r 国债利率
 * @param q = r
 * @param {number} sigma 隐含波动率
 * @param {number} time 时间。以年为单位
 * @returns {number}
 */
export function call(S, X, r, q, sigma, time) {
  const sigmaSqr = Math.pow(sigma, 2)
  const timeSqrt = Math.sqrt(time)
  const d1 =
    (Math.log(S / X) + (r - q + 0.5 * sigmaSqr) * time) / (sigma * timeSqrt)
  const d2 = d1 - sigma * timeSqrt
  return S * Math.exp(-q * time) * N(d1) - X * Math.exp(-r * time) * N(d2)
}

/**
 * 计算期权 put 价格
 * @param S
 * @param K
 * @param r
 * @param q
 * @param sigma
 * @param time
 * @returns {number}
 */
export function put(S, K, r, q, sigma, time) {
  const sigmaSqr = Math.pow(sigma, 2)
  const timeSqrt = Math.sqrt(time)
  const d1 =
    (Math.log(S / K) + (r - q + 0.5 * sigmaSqr) * time) / (sigma * timeSqrt)
  const d2 = d1 - sigma * timeSqrt
  return K * Math.exp(-r * time) * N(-d2) - S * Math.exp(-q * time) * N(-d1)
}

// 看涨期权价格近似值
export function getCallOptionPrice(S, X, r, b, sigma, time) {
  const sigmaSqr = sigma * sigma
  const sigmaSqrt = Math.sqrt(time)
  const nn = (2.0 * b) / sigmaSqr
  const m = (2.0 * r) / sigmaSqr
  const K = 1.0 - Math.exp(-r * time)
  const q2 = (-(nn - 1) + Math.sqrt(Math.pow(nn - 1, 2.0) + (4 * m) / K)) * 0.5
  // seed value from paper
  const q2Inf = 0.5 * (-(nn - 1.0) + Math.sqrt(Math.pow(nn - 1, 2.0) + 4.0 * m))
  const starInf = X / (1.0 - 1.0 / q2Inf)
  const h2 = -(b * time + 2.0 * sigma * sigmaSqrt) * (X / (starInf - X))
  const sSeed = X + (starInf - X) * (1.0 - Math.exp(h2))
  let noIterations = 0 // iterate on S to find aStar, using Newton steps
  let Si = sSeed
  let g = 1
  let gprime = 1.0
  while (
    Math.abs(g) > ACCURACY &&
    Math.abs(gprime) > ACCURACY && // to avoid exploding Newton's
    noIterations++ < 500 &&
    Si > 0.0
  ) {
    const c = call(Si, X, r, b, sigma, time)
    const d1 =
      (Math.log(Si / X) + (b + 0.5 * sigmaSqr) * time) / (sigma * sigmaSqrt)
    g =
      (1.0 - 1.0 / q2) * Si -
      X -
      c +
      (1.0 / q2) * Si * Math.exp((b - r) * time) * N(d1)
    gprime =
      (1.0 - 1.0 / q2) * (1.0 - Math.exp((b - r) * time) * N(d1)) +
      (1.0 / q2) *
        Math.exp((b - r) * time) *
        n(d1) *
        (1.0 / (sigma * sigmaSqrt))
    Si = Si - g / gprime
  }
  let aStar = 0
  aStar = Math.abs(g) > ACCURACY ? sSeed : Si
  let C = 0
  const c = call(S, X, r, b, sigma, time)
  if (S >= aStar) {
    C = S - X
  } else {
    const d1 =
      (Math.log(aStar / X) + (b + 0.5 * sigmaSqr) * time) / (sigma * sigmaSqrt)
    const A2 = (1.0 - Math.exp((b - r) * time) * N(d1)) * (aStar / q2)
    C = c + A2 * Math.pow(S / aStar, q2) || 0
  }
  return Math.max(C, c)
}

// 看跌期权价格近似值
export function getPutOptionPrice(S, X, r, b, sigma, time) {
  const sigmaSqr = sigma * sigma
  const sigmaSqrt = Math.sqrt(time)
  const M = (2.0 * r) / sigmaSqr
  const NN = (2.0 * b) / sigmaSqr
  const K = 1.0 - Math.exp(-r * time)
  const q1 =
    0.5 * (-(NN - 1) - Math.sqrt(Math.pow(NN - 1, 2.0) + (4.0 * M) / K))
  const qInf1 = 0.5 * (-(NN - 1) - Math.sqrt(Math.pow(NN - 1, 2.0) + 4.0 * M))
  const starStarInf = X / (1.0 - 1.0 / qInf1)
  const h1 = (b * time - 2 * sigma * sigmaSqrt) * (X / (X - starStarInf))
  const sSeed = starStarInf + (X - starStarInf) * Math.exp(h1)
  let Si = sSeed // now do Newton iterations to solve for S**
  let noIterations = 0
  let g = 1
  let gprime = 1
  while (
    Math.abs(g) > ACCURACY &&
    Math.abs(gprime) > ACCURACY && // to avoid non-convergence
    noIterations++ < 500 &&
    Si > 0.0
  ) {
    const p = put(Si, X, r, b, sigma, time)
    const d1 =
      (Math.log(Si / X) + (b + 0.5 * sigmaSqr) * time) / (sigma * sigmaSqrt)
    g = X - Si - p + ((1 - Math.exp((b - r) * time) * N(-d1)) * Si) / q1
    gprime =
      (1.0 / q1 - 1.0) * (1.0 - Math.exp((b - r) * time) * N(-d1)) +
      (1.0 / q1) *
        Math.exp((b - r) * time) *
        (1.0 / (sigma * sigmaSqrt)) *
        n(-d1)
    Si = Si - g / gprime
  }
  let Star = Si
  if (g > ACCURACY) {
    Star = sSeed
  }
  let P = 0
  const p = put(S, X, r, b, sigma, time)
  if (S > Star) {
    const d1 =
      (Math.log(Star / X) + (b + 0.5 * sigmaSqr) * time) / (sigma * sigmaSqrt)
    const A1 = -(Star / q1) * (1 - Math.exp((b - r) * time) * N(-d1))
    P = p + A1 * Math.pow(S / Star, q1)
  } else {
    P = X - S
  }
  return Math.max(P, p)
}

/**
 * 获取看涨期权风险指标
 * @param {number} S 股票价格
 * @param {number} K 行权价
 * @param {number} r 期权利率
 * @param {number} sigma 隐含波动率
 * @param {number} time 时间
 * @returns {{Gamma: *, Vega: *, Delta: *, Rho: *, Theta: *}}
 */
export function getCallOptionRiskIndicator(S, K, r, sigma, time) {
  const time_sqrt = Math.sqrt(time)
  const d1 =
    (Math.log(S / K) + r * time) / (sigma * time_sqrt) + 0.5 * sigma * time_sqrt
  const d2 = d1 - sigma * time_sqrt
  const delta = N(d1)
  const gamma = n(d1) / (S * sigma * time_sqrt)
  const theta =
    (-(S * sigma * n(d1)) / (2 * time_sqrt) -
      r * K * Math.exp(-r * time) * N(d2)) /
    365
  const vega = (S * time_sqrt * n(d1)) / 100
  const rho = K * time * Math.exp(-r * time) * N(d2)
  return { delta, gamma, theta, vega, rho }
}

// 获取看跌期权风险指标
export function getPutOptionRiskIndicator(S, K, r, sigma, time) {
  const time_sqrt = Math.sqrt(time)
  const d1 =
    (Math.log(S / K) + r * time) / (sigma * time_sqrt) + 0.5 * sigma * time_sqrt
  const d2 = d1 - sigma * time_sqrt
  const delta = -N(-d1)
  const gamma = n(d1) / (S * sigma * time_sqrt)
  const theta =
    (-(S * sigma * n(d1)) / (2 * time_sqrt) +
      r * K * Math.exp(-r * time) * N(-d2)) /
    365
  const vega = (S * time_sqrt * n(d1)) / 100
  const rho = -K * time * Math.exp(-r * time) * N(-d2)
  return { delta, gamma, theta, vega, rho }
}

/**
 * 求看涨期权的隐含波动率
 * @param {number} target 目标价
 * @param {number} S 股票 价格
 * @param {number} X 行权价格
 * @param {number} r 国债利率
 * @param {number} b =r
 * @param {number} time 时间。以年为单位
 */
export function getVolatilityCall(target, S, X, r, b, time) {
  let min = 0
  let max = 2.5
  let sig = min
  let x = 1000
  let maxCount = 100
  while (Math.abs(x - target) > 0.001 && maxCount-- > 0) {
    sig = (min + max) / 2
    x = getCallOptionPrice(S, X, r, b, sig, time)
    if (x < target) {
      min = sig
    } else if (x > target) {
      max = sig
    }
  }
  return sig
}

/**
 * 求看跌期权的隐含波动率
 * @param target 目标价
 * @param S 股票 价格
 * @param X 行权价格
 * @param r 国债利率
 * @param b =r
 * @param time 时间，以年为单位半年是0.5
 */
export function getVolatilityPut(target, S, X, r, b, time) {
  let min = 0
  let max = 2.5
  let sig = min
  let x = 1000
  let maxCount = 100
  while (Math.abs(x - target) > 0.001 && maxCount-- > 0) {
    sig = (min + max) / 2
    x = getPutOptionPrice(S, X, r, b, sig, time)
    if (x < target) {
      min = sig
    } else if (x > target) {
      max = sig
    }
  }
  return sig
}

/**
 * 计算买入盈利概率
 * @param {number} S 股票价格
 * @param {number} K 行权价格
 * @param {number} X 期权价格
 * @param {number} r 国债利率
 * @param {number} sigma 波动率
 * @param {number} time 时间
 * @returns {number}
 */
export function getBuyCallProfitRate(S, K, X, r, sigma, time) {
  const time_sqrt = Math.sqrt(time)
  const d1 = Math.log(S / (K + X)) + r * time
  const d2 = sigma * time_sqrt
  return N(d1 / d2)
}

/**
 * 计算买入盈利概率
 * @param {number} S 股票价格
 * @param {number} K 行权价格
 * @param {number} X 期权价格
 * @param {number} r 国债利率
 * @param {number} sigma 波动率
 * @param {number} time 时间
 * @returns {number}
 */
export function getBuyPutProfitRate(S, K, X, r, sigma, time) {
  const time_sqrt = Math.sqrt(time)
  const d1 = Math.log(S / (K - X)) + r * time
  const d2 = sigma * time_sqrt
  return 1 - N(d1 / d2)
}

/**
 * 获取卖出溢价率
 * @param {number} S 股票价格
 * @param {number} K 行权价
 * @param {number} X 期权价格
 * @returns {number}
 */
export function getPutPremiumRate(S, K, X) {
  return (K - X - S) / S
}

/**
 * 获取买入溢价率
 * @param {number} S 股票价格
 * @param {number} K 行权价
 * @param {number} X 期权价格
 * @returns {number}
 */
export function getCallPremiumRate(S, K, X) {
  return (K + X - S) / S
}

/**
 * 获取期权的时间价值
 * @param {number} K
 * @param {number} S
 * @param {number} X
 * @returns {*|string}
 */
export function getTimeValueCall(S, K, X) {
  let res = 0
  if (K > S) {
    res = X
  } else {
    res = X + K - S
  }
  return res
}
/**
 * 获取期权的时间价值
 * @param {number} K
 * @param {number} S
 * @param {number} X
 * @returns {*|string}
 */
export function getTimeValuePut(S, K, X) {
  let res = 0
  if (K < S) {
    res = X
  } else {
    res = X - (K - S)
  }
  return res
}
